'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Deal {
  id: number;
  propertyId: number;
  title: string;
  location: string;
  country: string;
  type: string;
  image: string;
  alt: string;
  originalPrice: number;
  discountedPrice: number;
  discountPercent: number;
  rating: number;
  reviews: number;
  currency: string;
  tag: string;
  tagColor: string;
  category: string;
  expiresIn?: string;
  isSuperhost?: boolean;
  badge?: string;
}

const deals: Deal[] = [
  {
    id: 1,
    propertyId: 1,
    title: 'Lakeside Villa with Private Pool',
    location: 'Entebbe',
    country: 'Uganda',
    type: 'Entire villa',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80',
    alt: 'Luxury lakeside villa with private pool and stunning water views',
    originalPrice: 280,
    discountedPrice: 196,
    discountPercent: 30,
    rating: 4.97,
    reviews: 124,
    currency: 'USD',
    tag: 'Weekend Deal',
    tagColor: 'bg-rose-500',
    category: 'weekend',
    expiresIn: '2d 14h',
    isSuperhost: true,
    badge: 'Guest favourite',
  },
  {
    id: 2,
    propertyId: 2,
    title: 'Modern Apartment in City Centre',
    location: 'Kampala',
    country: 'Uganda',
    type: 'Entire apartment',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
    alt: 'Modern city centre apartment with panoramic views of Kampala skyline',
    originalPrice: 150,
    discountedPrice: 120,
    discountPercent: 20,
    rating: 4.88,
    reviews: 89,
    currency: 'USD',
    tag: 'Early Bird',
    tagColor: 'bg-green-500',
    category: 'early-bird',
    expiresIn: '5d 8h',
    isSuperhost: false,
  },
  {
    id: 3,
    propertyId: 3,
    title: 'Riverside Cottage with Garden',
    location: 'Jinja',
    country: 'Uganda',
    type: 'Entire cottage',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
    alt: 'Charming riverside cottage surrounded by lush garden and tropical greenery',
    originalPrice: 200,
    discountedPrice: 150,
    discountPercent: 25,
    rating: 4.95,
    reviews: 67,
    currency: 'USD',
    tag: 'Long Stay',
    tagColor: 'bg-blue-500',
    category: 'long-stay',
    isSuperhost: true,
    badge: 'Superhost',
  },
  {
    id: 4,
    propertyId: 4,
    title: 'Boutique Hotel Suite Downtown',
    location: 'Kampala',
    country: 'Uganda',
    type: 'Hotel room',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
    alt: 'Elegant boutique hotel suite with premium furnishings in downtown Kampala',
    originalPrice: 180,
    discountedPrice: 126,
    discountPercent: 30,
    rating: 4.82,
    reviews: 203,
    currency: 'USD',
    tag: 'Last Minute',
    tagColor: 'bg-orange-500',
    category: 'last-minute',
    expiresIn: '0d 18h',
  },
  {
    id: 5,
    propertyId: 5,
    title: 'Safari Lodge Near National Park',
    location: 'Murchison Falls',
    country: 'Uganda',
    type: 'Entire lodge',
    image: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=800&q=80',
    alt: 'Rustic safari lodge with stunning views near Murchison Falls National Park',
    originalPrice: 350,
    discountedPrice: 280,
    discountPercent: 20,
    rating: 4.99,
    reviews: 56,
    currency: 'USD',
    tag: 'Early Bird',
    tagColor: 'bg-green-500',
    category: 'early-bird',
    expiresIn: '7d 2h',
    isSuperhost: true,
    badge: 'Rare find',
  },
  {
    id: 6,
    propertyId: 6,
    title: 'Beachfront Bungalow on Lake Victoria',
    location: 'Entebbe',
    country: 'Uganda',
    type: 'Entire bungalow',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&q=80',
    alt: 'Cozy beachfront bungalow with direct access to Lake Victoria shoreline',
    originalPrice: 220,
    discountedPrice: 154,
    discountPercent: 30,
    rating: 4.91,
    reviews: 142,
    currency: 'USD',
    tag: 'Weekend Deal',
    tagColor: 'bg-rose-500',
    category: 'weekend',
    expiresIn: '1d 6h',
  },
  {
    id: 7,
    propertyId: 7,
    title: 'Mountain View Chalet',
    location: 'Kabale',
    country: 'Uganda',
    type: 'Entire chalet',
    image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&q=80',
    alt: 'Cozy mountain chalet with panoramic views of the Kabale highlands',
    originalPrice: 160,
    discountedPrice: 112,
    discountPercent: 30,
    rating: 4.86,
    reviews: 78,
    currency: 'USD',
    tag: 'Long Stay',
    tagColor: 'bg-blue-500',
    category: 'long-stay',
    isSuperhost: true,
  },
  {
    id: 8,
    propertyId: 8,
    title: 'Urban Loft with Rooftop Terrace',
    location: 'Kampala',
    country: 'Uganda',
    type: 'Entire loft',
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
    alt: 'Stylish urban loft featuring a private rooftop terrace with city views',
    originalPrice: 130,
    discountedPrice: 91,
    discountPercent: 30,
    rating: 4.79,
    reviews: 95,
    currency: 'USD',
    tag: 'Last Minute',
    tagColor: 'bg-orange-500',
    category: 'last-minute',
    expiresIn: '0d 9h',
  },
];

const categories = [
  { id: 'all', label: 'All Deals', icon: '🏷️' },
  { id: 'weekend', label: 'Weekend Getaway', icon: '🌅' },
  { id: 'early-bird', label: 'Early Bird', icon: '🐦' },
  { id: 'long-stay', label: 'Long Stay', icon: '🏡' },
  { id: 'last-minute', label: 'Last Minute', icon: '⚡' },
];

const heroBanners = [
  {
    id: 1,
    title: 'Weekend Getaway',
    subtitle: 'Fri–Sun stays up to 30% off',
    description: 'Escape the city for a weekend. Book now and save big on top-rated stays.',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80',
    alt: 'Luxury resort pool perfect for a weekend getaway',
    tag: 'Up to 30% off',
    tagColor: 'bg-rose-500',
    category: 'weekend',
  },
  {
    id: 2,
    title: 'Early Bird Savings',
    subtitle: 'Book 30+ days ahead, save 20%',
    description: 'Plan ahead and pocket the savings. The earlier you book, the more you save.',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80',
    alt: 'Serene villa with pool available for early bird booking',
    tag: 'Save 20%',
    tagColor: 'bg-green-500',
    category: 'early-bird',
  },
  {
    id: 3,
    title: 'Long Stay Offer',
    subtitle: '7+ nights, 25% discount applied',
    description: 'Stay longer, save more. Perfect for remote workers and extended vacations.',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80',
    alt: 'Spacious apartment ideal for long stay guests',
    tag: 'Save 25%',
    tagColor: 'bg-blue-500',
    category: 'long-stay',
  },
  {
    id: 4,
    title: 'Last Minute Deals',
    subtitle: 'Tonight & tomorrow at great prices',
    description: 'Spontaneous traveller? Grab tonight\'s best deals before they\'re gone.',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=80',
    alt: 'Cozy hotel room available for last minute booking',
    tag: 'Tonight',
    tagColor: 'bg-orange-500',
    category: 'last-minute',
  },
];

function CountdownBadge({ expiresIn }: { expiresIn: string }) {
  const isUrgent = expiresIn.startsWith('0d');
  return (
    <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${isUrgent ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Ends in {expiresIn}
    </div>
  );
}

function DealCard({ deal }: { deal: Deal }) {
  const [liked, setLiked] = useState(false);
  const savings = deal.originalPrice - deal.discountedPrice;

  return (
    <Link href={`/property/${deal.propertyId}`} className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="relative h-52 overflow-hidden">
        <AppImage
          src={deal.image}
          alt={deal.alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Tag */}
        <div className={`absolute top-3 left-3 ${deal.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
          {deal.tag}
        </div>

        {/* Discount badge */}
        <div className="absolute top-3 right-10 bg-white text-gray-900 text-xs font-black px-2 py-1 rounded-full shadow-md">
          -{deal.discountPercent}%
        </div>

        {/* Wishlist */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLiked(!liked); }}
          className="absolute top-3 right-3 p-1 transition-transform hover:scale-110"
          aria-label={liked ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg
            className={`w-5 h-5 transition-colors drop-shadow-md ${liked ? 'text-rose-500 fill-rose-500' : 'text-white fill-white/30 stroke-white'}`}
            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
        </button>

        {/* Savings pill at bottom */}
        <div className="absolute bottom-3 left-3">
          <span className="bg-white/95 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            Save {deal.currency} {savings}/night
          </span>
        </div>
      </div>

      <div className="p-4">
        {/* Expiry */}
        {deal.expiresIn && (
          <div className="mb-2">
            <CountdownBadge expiresIn={deal.expiresIn} />
          </div>
        )}

        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1 min-w-0">
            {deal.isSuperhost && (
              <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full mr-1">Superhost</span>
            )}
            <h3 className="text-sm font-bold text-gray-900 truncate mt-1">{deal.title}</h3>
            <p className="text-xs text-gray-500 truncate">{deal.location}, {deal.country}</p>
            <p className="text-xs text-gray-400">{deal.type}</p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <svg className="w-3 h-3 fill-gray-900" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-xs font-bold text-gray-900">{deal.rating.toFixed(2)}</span>
            <span className="text-xs text-gray-400 ml-0.5">({deal.reviews})</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <span className="text-base font-black text-gray-900">{deal.currency} {deal.discountedPrice}</span>
          <span className="text-sm text-gray-400 line-through">{deal.currency} {deal.originalPrice}</span>
          <span className="text-xs text-gray-500">/ night</span>
        </div>
      </div>
    </Link>
  );
}

export default function SpecialOffersPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeBanner, setActiveBanner] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
    const interval = setInterval(() => {
      setActiveBanner((prev) => (prev + 1) % heroBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const filteredDeals = activeCategory === 'all'
    ? deals
    : deals.filter((d) => d.category === activeCategory);

  const banner = heroBanners[activeBanner];

  const totalSavings = filteredDeals.reduce((sum, d) => sum + (d.originalPrice - d.discountedPrice), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Banner */}
      <div className="relative h-[480px] overflow-hidden">
        <div className="absolute inset-0 transition-opacity duration-700">
          <AppImage
            src={banner.image}
            alt={banner.alt}
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-6 sm:px-10 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/70 text-sm mb-6">
            <Link href="/home" className="hover:text-white transition-colors">Home</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white font-medium">Special Offers & Deals</span>
          </div>

          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className={`inline-flex items-center gap-2 ${banner.tagColor} text-white text-sm font-bold px-3 py-1.5 rounded-full mb-4`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {banner.tag}
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-3 leading-tight">{banner.title}</h1>
            <p className="text-white/80 text-lg mb-2">{banner.subtitle}</p>
            <p className="text-white/60 text-sm max-w-md">{banner.description}</p>
          </div>

          {/* Banner dots */}
          <div className="flex gap-2 mt-6">
            {heroBanners.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveBanner(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === activeBanner ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-6 sm:gap-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Active deals</p>
              <p className="text-sm font-bold text-gray-900">{deals.length} offers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Avg. savings shown</p>
              <p className="text-sm font-bold text-gray-900">Up to 30% off</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">Limited time</p>
              <p className="text-sm font-bold text-gray-900">Ends soon</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <svg className="w-4 h-4 text-amber-500 fill-amber-500" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm font-bold text-gray-900">Top-rated properties only</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">

        {/* Category filter tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400 hover:shadow-sm'
              }`}
            >
              <span>{cat.icon}</span>
              {cat.label}
              {cat.id !== 'all' && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeCategory === cat.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {deals.filter((d) => d.category === cat.id).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Section heading */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900">
              {activeCategory === 'all' ? 'All Deals' : categories.find((c) => c.id === activeCategory)?.label}
            </h2>
            <p className="text-gray-500 text-sm mt-0.5">
              {filteredDeals.length} deal{filteredDeals.length !== 1 ? 's' : ''} available
              {activeCategory !== 'all' && ` · Avg. saving USD ${Math.round(totalSavings / filteredDeals.length)}/night`}
            </p>
          </div>
          <Link
            href="/search"
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-gray-700 border border-gray-300 px-4 py-2 rounded-full hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search all stays
          </Link>
        </div>

        {/* Deals grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-14">
          {filteredDeals.map((deal, i) => (
            <div
              key={deal.id}
              className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <DealCard deal={deal} />
            </div>
          ))}
        </div>

        {/* How deals work — asymmetric bento layout */}
        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm mb-10">
          <h2 className="text-xl font-black text-gray-900 mb-6">How our deals work</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2 bg-rose-50 rounded-2xl p-6">
              <div className="w-10 h-10 bg-rose-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Verified discounts</h3>
              <p className="text-sm text-gray-600">Every deal is verified against the property's standard rate. No inflated prices, just real savings.</p>
            </div>
            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-2xl p-5">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mb-3">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">Limited time</h3>
                <p className="text-xs text-gray-600">Deals expire. Book before the countdown hits zero to lock in your price.</p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-5">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mb-3">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">No hidden fees</h3>
                <p className="text-xs text-gray-600">The discounted price you see is what you pay. Cleaning fees shown separately.</p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-5 sm:col-span-2">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">Top-rated only</h3>
                    <p className="text-xs text-gray-600">Only properties with 4.7+ ratings qualify for our deals programme. Quality is never discounted.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="relative rounded-3xl overflow-hidden">
          <AppImage
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80"
            alt="Scenic mountain landscape perfect for a holiday getaway"
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-gray-900/40" />
          <div className="relative z-10 px-8 py-12 sm:py-16 max-w-lg">
            <p className="text-rose-400 text-sm font-bold uppercase tracking-widest mb-3">Don't miss out</p>
            <h2 className="text-3xl font-black text-white mb-3 leading-tight">New deals added every week</h2>
            <p className="text-white/70 text-sm mb-6">Sign up to get notified when new deals drop for your favourite destinations.</p>
            <div className="flex gap-2">
              <Link
                href="/signup"
                className="bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-bold px-6 py-3 rounded-full text-sm transition-colors"
              >
                Get deal alerts
              </Link>
              <Link
                href="/search"
                className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors border border-white/30"
              >
                Browse all stays
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
